import {
  makeStyles,
  Alert,
  Collapse,
  IconButton,
  ConfirmationDialog,
  Grid,
  CheckIcon,
  Checkbox,
  FormControlLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputField
} from '@paintscout/ui'
import type { DialogProps } from '@paintscout/ui'
import {
  SingleDatePicker,
  KeypadInputField,
  DropdownSelect,
  useDialogs,
  useUser,
  useClientOptions
} from '@paintscout/ui-business'
import {
  getPaymentMethodOptions,
  getPayments,
  getQuoteOptions,
  hasIntegrationInterface,
  setPayments,
  uuid
} from '@paintscout/util/builder'
import moment from 'moment'
import type { Payment, PayrixPaymentType, QuoteDocument } from 'paintscout'
import React, { useState, useRef } from 'react'
import SendIcon from '@material-ui/icons/Send'
import { useQuoteMutations } from 'shared/hooks'
import { useQuoteQuery } from '@paintscout/api'
import { useSnackbar } from 'notistack'
import { isLooselyEqual } from '@paintscout/util'
import { parseNumber } from '@paintscout/util/calculator'
import CloseIcon from '@material-ui/icons/Close'
import SendPaymentRequestDialog from '../SendPaymentRequestDialog'
import TileListDialog from '../TileListDialog'
import CollectPayrixPaymentDialog from '../CollectPayrixPaymentDialog'
const CollectStripePaymentDialog = React.lazy(() => import('../CollectStripePaymentDialog'))
import { useQuote } from '../../context/useQuote'

export interface PaymentsDialogProps extends DialogProps {
  quote?: QuoteDocument // Deals dont have full quote passed in so we search our own by id instead
  quoteId?: string
  paymentIndex?: number
  backOnCancel?: boolean
  markingPaid?: boolean
  onDeal?: boolean
  canDelete?: boolean
  hideDeposit?: boolean
  onConfirm?: (quote: QuoteDocument) => void
  onCancel?: () => void
}

export interface PaymentsDialogState {
  existing: boolean
  payment: Payment
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateRows: '1fr',
    gridGap: theme.spacing()
  },
  overflowVisible: {
    overflow: 'visible'
  },
  buttonContainer: {
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    '&:hover': {
      '& span': {
        color: `${theme.palette.error.main}`
      },
      color: theme.palette.error.main
    }
  },
  markPaidButton: {
    backgroundColor: theme.paintscout.palette.semantic.Success.S60
  },
  resendButton: { color: theme.palette.text.secondary, border: `1px solid ${theme.palette.text.hint}` },
  dialogPaper: {
    '& .MuiDialogActions-root': {
      display: 'none !important'
    },
    '& .MuiDialogContent-root': {
      paddingBottom: `${theme.spacing(3)}px !important`
    }
  }
}))

function PaymentsDialog(props: PaymentsDialogProps) {
  const {
    open,
    paymentIndex,
    markingPaid,
    onConfirm,
    onCancel,
    quote,
    quoteId,
    onDeal,
    backOnCancel,
    canDelete = true,
    hideDeposit = false,
    ...baseDialogProps
  } = props
  const { dismissDialog, dismissAllDialogs, openDialog } = useDialogs()
  const { options, clientId } = useClientOptions()
  const { enqueueSnackbar } = useSnackbar()
  const { saveQuote } = useQuoteMutations()
  const { user, preferences } = useUser()
  const { updateQuote } = useQuote()
  const classes = useStyles(props)
  const { loading, error } = useQuoteQuery({
    skip: !quoteId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { id: quoteId },
    onCompleted: (data) => {
      setFullDoc(data.quote as QuoteDocument)
    }
  })
  const { allowPaymentsOnInvoices, allowACHPaymentsOnInvoices } = getQuoteOptions({ quote, options })
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const isCanadianPayrix = options.options.integrations?.payrix?.config?.country === 'Canada' && hasPayrix
  const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'

  const hasPaymentProcessing = hasStripe || hasStripeW1d || hasPayrix
  const isPaymentEligible = quote.totals.after_tax > 0 && quote.totals.balance_due > 0

  const canCollectCCPayment = hasPaymentProcessing && allowPaymentsOnInvoices && isPaymentEligible
  const canCollectACHPayment = hasPayrix && allowACHPaymentsOnInvoices && isPaymentEligible

  const [fullDoc, setFullDoc] = useState<QuoteDocument>(quote)
  const [isLoading, setIsLoading] = useState(false)
  const [validationError, setValidationError] = useState(false)
  const existingPayment = typeof paymentIndex !== 'undefined' && getPayments({ quote: fullDoc })[paymentIndex]
  const isRequest = !!existingPayment?.id && existingPayment.status === 'pending'

  const initialPayment: Payment =
    markingPaid || existingPayment
      ? {
          ...existingPayment,
          ...(markingPaid
            ? {
                date: Date.now()
              }
            : {})
        }
      : {
          id: uuid(),
          type: null,
          amount: fullDoc?.totals?.balance_due,
          date: Date.now(),
          note: '',
          deposit: false,
          manual: !isRequest,
          status: 'paid'
        }

  const initialPaymentRef = useRef<Payment>(initialPayment)
  const hasUserChangesRef = useRef(false)
  const [payment, setPayment] = useState<Payment>(initialPayment)

  const dateValue = moment(payment.date)
  const typeOptions = getPaymentMethodOptions(options).map((option) => ({
    ...option,
    value: option.label
  }))
  const typeValue = payment.type
    ? {
        key: payment.type,
        label: payment.type,
        value: payment.type
      }
    : null

  const leftButton = markingPaid ? (
    <Button onClick={handleClose} variant="textNoPadding">
      {backOnCancel ? '< Back' : 'Cancel'}
    </Button>
  ) : !existingPayment || !canDelete ? (
    <></>
  ) : (
    <Button
      onClick={handleDeleteClick}
      loading={isLoading}
      disabled={!!loading || !!error || !fullDoc || isLoading}
      variant="textNoPadding"
      className={classes.deleteButton}
    >
      Delete
    </Button>
  )

  const defaultTitle = `${existingPayment ? (isRequest ? 'Edit' : 'View') : 'Add'} Payment${isRequest ? ' Request' : ''}`
  const readonlyFields = existingPayment && !isRequest && !markingPaid
  const title = markingPaid ? 'Mark as Paid' : defaultTitle
  const isViewMode = title === 'View Payment'

  function hasChanges(): boolean {
    if (hasUserChangesRef.current) {
      return true
    }
    // Normalize payment objects for comparison (handle date as unix timestamp)
    const currentPayment = {
      ...payment,
      date: typeof payment.date === 'number' ? payment.date : moment(payment.date).valueOf()
    }
    const initialPaymentForComparison = {
      ...initialPaymentRef.current,
      date:
        typeof initialPaymentRef.current.date === 'number'
          ? initialPaymentRef.current.date
          : moment(initialPaymentRef.current.date).valueOf()
    }
    return !isLooselyEqual(currentPayment, initialPaymentForComparison)
  }

  function handleClose() {
    if (hasChanges()) {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dismissDialog() // Dismiss confirmation dialog
          dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      if (onCancel) {
        onCancel()
      }
      dismissAllDialogs()
    }
  }

  return (
    <Dialog
      classes={{ paper: isViewMode ? `${classes.overflowVisible} ${classes.dialogPaper}` : classes.overflowVisible }}
      open={open}
      maxWidth="md"
      onClose={handleClose}
      {...baseDialogProps}
    >
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={handleClose} disabled={isLoading || loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        {title}
      </DialogTitle>
      <DialogContent className={classes.overflowVisible}>
        <div className={classes.root}>
          {(!isRequest || markingPaid) && (
            <DropdownSelect
              variant="single"
              value={typeValue}
              label={'Type/Method'}
              fullWidth={true}
              name={'type'}
              onChange={handleTypeChange}
              options={typeOptions}
              searchable
              placeholder={{
                selectPlaceholder: 'Select a Payment Method',
                plural: 'Payment Methods'
              }}
              disabled={readonlyFields}
              popperModifiers={{
                flip: {
                  enabled: false
                },
                preventOverflow: {
                  enabled: false
                }
              }}
              required
            />
          )}

          <Collapse show={validationError}>
            <Alert severity="error" noMargin>
              Please select a payment method
            </Alert>
          </Collapse>
          <KeypadInputField
            required
            label={'Amount'}
            value={payment.amount}
            fullWidth={true}
            format={'price'}
            name={'amount'}
            onChange={handleKeypadChange}
            disabled={readonlyFields}
          />
          <SingleDatePicker
            required
            label={isRequest && !markingPaid ? 'Date Sent' : 'Payment Date'}
            date={dateValue}
            onChange={handleDateChange}
            disabled={readonlyFields}
          />

          <InputField
            label={'Note'}
            value={payment.note}
            multiline={true}
            maxRows={4}
            name={'note'}
            fullWidth={true}
            onChange={handleChange}
            disabled={readonlyFields}
          />
          {!isRequest && !hideDeposit && (
            <FormControlLabel
              label={'This payment is a deposit'}
              disabled={readonlyFields}
              control={<Checkbox name={'deposit'} checked={payment.deposit} onChange={handleCheckboxChange} />}
            />
          )}
          {isRequest && !markingPaid && (
            <Grid container spacing={2} justifyContent="space-between" className={classes.buttonContainer}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="dialog"
                  className={classes.resendButton}
                  aria-label="Resend"
                  onClick={handleResend}
                  icon={SendIcon}
                >
                  Resend
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  className={classes.markPaidButton}
                  aria-label="Mark As Paid"
                  onClick={handleMarkAsPaid}
                  icon={CheckIcon}
                >
                  Mark As Paid
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      </DialogContent>
      {(isViewMode || canDelete) && (
        <DialogActions leftButton={leftButton}>
          {!isViewMode && (
            <Button
              type="submit"
              variant={'contained'}
              onClick={handleConfirm}
              loading={isLoading || loading}
              disabled={validationError || !!error || !fullDoc}
            >
              Save
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  )

  function handleChange(event: any) {
    const { value, name, type } = event.target
    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      [name]: type === 'number' ? Number(value) : value
    })
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    const { name } = event.target

    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      [name]: !!checked
    })
  }

  function handleKeypadChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target

    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      [name]: Number(value)
    })
  }

  function handleDateChange(value: moment.Moment) {
    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      date: value?.unix() * 1000
    })
  }

  function handleTypeChange(value: any) {
    hasUserChangesRef.current = true
    setPayment({
      ...payment,
      type: value?.value
    })
    setValidationError(false)
  }

  function handleResend() {
    openDialog(SendPaymentRequestDialog, {
      payment,
      user,
      preferences,
      isResend: true
    })
  }

  function handleMarkAsPaid() {
    openDialog(TileListDialog, {
      title: 'Mark as Paid',
      showTopClose: true,
      items: [
        {
          key: 'record-payment',
          label: 'Record Payment'
        },
        canCollectCCPayment && {
          key: 'credit',
          label: 'Take Credit Card Payment'
        },
        canCollectACHPayment && {
          key: 'ach',
          label: `Take ${achLabel} Payment`
        }
      ].filter(Boolean),
      onItemSelect: (key: string) => {
        if (key === 'record-payment') {
          openDialog(PaymentsDialog, {
            quote: fullDoc,
            paymentIndex,
            backOnCancel: true,
            markingPaid: true,
            onConfirm: async (quote) => {
              if (onDeal) {
                const { quote: savedQuote } = await saveQuote({
                  quote: {
                    ...quote,
                    dates: {
                      ...quote.dates,
                      updated: Date.now()
                    }
                  }
                })
                onConfirm(savedQuote)
              } else {
                if (onConfirm) {
                  onConfirm(quote)
                }
              }
              dismissAllDialogs()
            },
            onCancel: dismissDialog
          })
        } else {
          handleCollectPayment({
            amount: payment.amount,
            note: payment.note,
            deposit: payment.deposit,
            paymentRequestId: payment.id,
            paymentType: key as PayrixPaymentType
          })
        }
      }
    })
  }

  function handleCollectPayment({
    amount,
    note,
    deposit,
    paymentRequestId,
    paymentType
  }: {
    amount: number
    note: string
    deposit: boolean
    paymentRequestId?: string
    paymentType?: PayrixPaymentType
  }) {
    if (!hasPaymentProcessing) {
      enqueueSnackbar(
        'Something is wrong with your payment integration. Please contact our support team for assistance.',
        { variant: 'error' }
      )
      dismissAllDialogs()
      return
    }
    const PaymentDialog = hasPayrix ? CollectPayrixPaymentDialog : CollectStripePaymentDialog
    openDialog(PaymentDialog, {
      quote: fullDoc,
      amount,
      note,
      options,
      companyId: clientId,
      isDeposit: deposit,
      source: 'collect-payment',
      paymentRequestId,
      paymentType,
      backOnCancel: true,
      onCancel: dismissDialog,
      onConfirm: async (updatedQuote: QuoteDocument) => {
        dismissAllDialogs()
        if (onDeal) {
          const { quote: savedQuote } = await saveQuote({
            quote: {
              ...updatedQuote,
              dates: {
                ...updatedQuote.dates,
                updated: Date.now()
              }
            }
          })
          onConfirm(savedQuote)
        } else {
          await updateQuote({ quote: updatedQuote, autosave: false, blockDirty: true })
        }
      }
    })
  }

  function handleDeleteClick() {
    openDialog(ConfirmationDialog, {
      message: `Are you sure you want to remove this payment${payment?.status === 'pending' ? ' request' : ''}?`,
      onConfirm: async () => {
        await handleDelete()
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  async function handleDelete() {
    setIsLoading(true)
    const payments = getPayments({ quote: fullDoc }).filter((_, index) => index !== paymentIndex)
    const updatedQuote = setPayments({ quote: fullDoc, payments, options })
    if (onDeal) {
      const { quote: savedQuote } = await saveQuote({
        quote: {
          ...updatedQuote,
          dates: {
            ...updatedQuote.dates,
            updated: Date.now()
          }
        }
      })
      onConfirm(savedQuote)
    } else {
      updateQuote({ quote: updatedQuote, autosave: true })
      if (onConfirm) {
        onConfirm(updatedQuote)
      }
    }
    setIsLoading(false)
  }

  async function handleConfirm() {
    if (!isRequest && !payment.type && payment.status === 'paid') {
      setValidationError(true)
    } else {
      setIsLoading(true)
      console.log({ fullDoc })
      const payments = getPayments({ quote: fullDoc })
      // != null is intentional, catches both null and undefined
      if (paymentIndex != null) {
        payments[paymentIndex] = {
          ...payment,
          // payment.amount is typed `number`, but at runtime can be a formatted input string
          // ("1,000.00"). parseNumber strips separators and passes numbers through unchanged.
          amount: parseNumber(payment.amount),
          status: markingPaid ? 'paid' : payment.status,
          note: payment.note || '',
          date: payment.date
        }
      } else {
        payments.push({
          ...payment,
          amount: parseNumber(payment.amount)
        })
      }

      console.log({ payments })
      const updatedQuote = setPayments({ quote: fullDoc, payments, options })
      console.log({ updatedQuote })
      if (onDeal) {
        const { quote: savedQuote } = await saveQuote({
          quote: {
            ...updatedQuote,
            dates: {
              ...updatedQuote.dates,
              updated: Date.now()
            }
          }
        })

        setIsLoading(false)
        onConfirm(savedQuote)
        return
      } else {
        updateQuote({ quote: updatedQuote, autosave: true })
        if (onConfirm) {
          onConfirm(updatedQuote)
        } else {
          setIsLoading(false)
        }
      }
    }
  }
}

export default PaymentsDialog
